import { Auth } from 'aws-amplify';

import { store } from '@/store';
import { bus } from '@/main';
import { apiGet, apiPost } from './api';

/**
 * Modify the session and user state to reflect the impersonated user
 * @param {'locum' | 'practice'} type Type of user
 * @param {string} id Cognito ID of the impersonated user
 * @returns {boolean} Whether the personation succeeded
 */
export const setImpersonatedUser = async (type, id) => {
	try {
		const user = await Auth.currentAuthenticatedUser();
		if (id) {
			// Temporarily change the zoneinfo so that we can check if the user exists
			try {
				await Auth.updateUserAttributes(user, {
					zoneinfo: id,
				});
			} catch (error) {
				console.error('Error updating user attributes:', error);
			}
			const gen = setUser(type, id);
			const result = await gen.next();
			if (!result.value) {
				// User doesn't exist so reset the zoneinfo for admin
				await Auth.updateUserAttributes(user, {
					zoneinfo: '',
				});
				return false;
			}
			await updateZoneInfoAndSetCache(type, user, id);
			await gen.next();
		} else {
			await updateZoneInfoAndSetCache(type, user, id);
			store.commit('SET_USER_OVERWRITE', user.attributes);
		}

		return true;
	} catch (err) {
		console.error(err);
		return false;
	}
};

/**
 * Helper function to update the zoneinfo and localStorage cache
 * @param {'locum' | 'practice'} type Type of user
 * @param {object} user Amplify auth user
 * @param {string} id Cognito ID of the impersonated user
 */
const updateZoneInfoAndSetCache = async (type, user, id) => {
	await Auth.updateUserAttributes(user, {
		zoneinfo: id || '',
	});
	setImpersonationCache(!!id, type);
};

/**
 * Helper function to determine if the current impersonation is locum or practice
 * @param {boolean} isAdmin Is the current user an admin?
 * @returns {object}
 */
export const getImpersonationData = isAdmin => {
	const { impersonationMode, userType } = getImpersonationCache();
	const isImpersonation = isAdmin && impersonationMode;
	const isImpersonatedLocum = isImpersonation && userType === 'locum';
	const isImpersonatedPractice = isImpersonation && userType === 'practice';

	return { isImpersonatedLocum, isImpersonatedPractice };
};

/**
 * Set localStorage for the impersonation state. Ensures that we retain this state across refreshes.
 * @param {boolean} impersonationMode
 */
const setImpersonationCache = (impersonationMode, type) => {
	localStorage.setItem('impersonationMode', impersonationMode ? 'true' : 'false');
	localStorage.setItem('userType', type);
	bus.$emit('impersonationModeChange', impersonationMode);
};

/**
 * Get impersonation state from localStorage
 * @returns {boolean} Impersonation state
 */
export const getImpersonationCache = () => ({ impersonationMode: localStorage.getItem('impersonationMode') === 'true', userType: localStorage.getItem('userType') });

/**
 * Get Cognito user details and store it in state. The generator first attempts to get the profile data. If that fails then the generator yields false so that it doesn't proceed further. However, if it successeds
 * it will yield true so that the zoneinfo and cache can be updated before storing the data in the store.
 * @param {'locum' | 'practice'} type Type of user
 * @param {string} id Cognito ID of the impersonated user
 * @returns {boolean} Whether the user was successfully set
 */
export async function* setUser(type, id) {
	if (type === 'locum') {
		let professional;
		try {
			const response = await apiGet('professionals');
			professional = response.data;
			yield true;
		} catch (err) {
			console.log('fail!', err);
			yield false;
		}

		if (professional) {
			store.commit('SET_USER', { ...professional.data, tenantId: id }); //get the state and commit.
			store.dispatch('GET_USER_AVATAR');
		}
	} else if (type === 'practice') {
		let data;
		try {
			const response = await apiPost('users/cognito/get-user', { id });
			data = response.data;
			yield true;
		} catch (err) {
			yield false;
		}

		if (data) {
			const userObj = data.UserAttributes.reduce((acc, { Name, Value }) => {
				acc[Name] = Value;
				return acc;
			}, {});
			userObj.id = userObj.sub;
			delete userObj.sub;
			store.commit('SET_USER', userObj);
		}

		try {
			const { data: practices } = await apiGet('companies/mypractices');

			store.commit('SET_COMPANIES', practices.data);

			const companyId = localStorage.getItem('selectedCompany');

			let company = null;
			if (practices.data && practices.data.length > 0) {
				if (companyId) {
					company = practices.data.find(x => x.id === companyId);
					if (!company) company = practices.data[0];
				} else {
					company = practices.data[0];
				}
			}

			await store.dispatch('company/SetCompany', company, { root: true });
			store.dispatch('GET_PRACTICE_LOGO');

			// Resetting offers, bookings and availabilities
			store.commit('offers/SET_OFFERS', []);
			store.commit('bookings/SET_COMPANY_BOOKINGS', []);
			store.commit('availability/SET_AVAILABILITY', []);
		} catch {
			yield false;
		}
	}
	return true;
}

/**
 * Sign out of app and reset impersonation mode to return to admin next time
 */
export const authSignOut = async () => {
	try {
		await setImpersonatedUser();

		await Auth.signOut();

		sessionStorage.removeItem('featuredLocumsDismissed');
		window.location = store.getters.getAUTH;
	} catch (err) {
		alert('Error signing out');
	}
};
